import { ApartmentDataService } from ".";
import { ApartmentOnboarding } from "./Onboarding";

export type InvitedApartmentUserData = {
  username: string;
  apartmentRole: ApartmentRole;
};

export type CreateApartmentUserData = InvitedApartmentUserData & {
  password: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  apartmentRole: ApartmentRole;
  permissions: ApartmentUserPermissions;
};

export interface ApartmentUser {
  id: string;
  name: string;
  username?: string;
  phone?: string;
  role: string;
  addedById?: string;
  permissions: ApartmentUserPermissions;
}

export interface ApartmentUserPermissions {
  canReserveAmenities: boolean;
  domotics: {
    lights: boolean;
    clima: boolean;
    alarm: boolean;
    shutters: boolean;
  };
}

export enum ApartmentRole {
  Owner = "apartment_owner",
  Admin = "apartment_admin",
  Guest = "apartment_guest",
}
export const assignableApartmentRoles = {
  [ApartmentRole.Admin]: "Admin",
  [ApartmentRole.Guest]: "Ospite",
};

export enum DomoticsProvider {
  SCHNEIDER_ELECTRIC = "SCHNEIDER_ELECTRIC",
  CRESTRON = "CRESTRON",
}

export const domoticsProviderOpts = Object.values(DomoticsProvider);

export interface ApartmentWiserOpts {
  wiserUrl?: string;

  /**
   * Address to call to know whether the current alarm is ON or OFF.
   *
   * @type {string}
   * @memberof ApartmentWiserOpts
   */
  getGeneralAlarmAddress?: string;
  setGeneralAlarmAddress?: string;

  /**
   * Address to invoke in order to set ON or OFF all the fan devices in the
   * apartment.
   *
   * @type {string}
   * @memberof ApartmentWiserOpts
   */
  setGeneralFanAddress?: string;
  /**
   * Address to invoke in order to set ON or OFF all the lights in the apartment
   *
   * @type {string}
   * @memberof ApartmentWiserOpts
   */
  setGeneralLightsAddress?: string;

  /**
   * Address to invoke in order to set ON or OFF all the shutters in the
   * apartment.
   *
   * @type {string}
   * @memberof ApartmentWiserOpts
   */
  setGeneralShuttersMoveAddress?: string;
  /**
   * Address to invoke in order to set the same direction on ALL the shutters in
   * the apartment.
   *
   * Example: setting this to UP will set all the shutters direction to UP.
   * Invoking `setGeneralShuttersMoveAddress` to ON will move all the shutters
   * UP.
   *
   * @type {string}
   * @memberof ApartmentWiserOpts
   */
  setGeneralShuttersStatusAddress?: string;
  /**
   * Address to invoke to know whether the current apartment mode for clima is
   * winter or summer (gets the setpoint).
   *
   * There is currently no documentation about it, although it looks like cases
   * are the following:
   * 0 = summer
   * 1 = winter
   *
   * @type {string}
   * @memberof ApartmentWiserOpts
   */
  getWinterSummerAddress?: string;
  /**
   * Address to invoke to set the summer or winter mode.
   * Check above to see possible values.
   *
   * @type {string}
   * @memberof ApartmentWiserOpts
   */
  setWinterSummerAddress?: string;
}

export interface Apartment {
  id: string;
  name: string;
  code: string;
  building: {
    id: string;
    name: string;
  };
  users: ApartmentUser[];
  owner: ApartmentOwner;
  reservationConductStatus?: {
    /**
     * Used to keep track when the apartment was lastly banned from reserving.
     */
    lastDidntShowUpBanDate?: Date;
    /**
     * Used to keep track when the apartment was lastly unlocked for going
     * back to reserving.
     */
    lastUnlockDate?: Date;
    /**
     * Used to check whether such apartment can reserve amenities.
     */
    blocked?: boolean;
  };
  reservationOptions?: ApartmentDataService[];
  domoticsProvider?: DomoticsProvider;
  wiserOpts?: ApartmentWiserOpts;
  floorInfo?: String;

  /**
   * Returns whether the alarm is on or off.
   *
   * @type {boolean}
   * @memberof Apartment
   */
  currentAlarmStatus?: boolean;
  /**
   * Returns whether the clima mode is winter or summer.
   *
   * @type {('winter' | 'summer')}
   * @memberof Apartment
   */
  currentClimaMode?: "winter" | "summer";

  shouldCompleteSurvey?: boolean;
  lastSurveyId?: string;
  lastSurveyCompletedDate?: number;
  underConstruction?: boolean;
  onboarding?: ApartmentOnboarding;
  tag?: string;
}

export interface ApartmentOwner {
  id: string;
  name: string;
  email: string;
  phone: string;
}
